import { Input } from "@/components/Form/Input"
import type { SubscriptionPlan } from "@/hooks/useSubscriptionPlans"
import type { TeamAccessChoice, TeamAccessMode } from "@/lib/createTeamAccess"

type Props = {
  value: TeamAccessChoice
  onChange: (next: TeamAccessChoice) => void
  plans: SubscriptionPlan[]
  disabled?: boolean
}

const OPTIONS: Array<{ mode: TeamAccessMode; title: string; description: string }> = [
  {
    mode: "comp",
    title: "Comp a plan",
    description:
      "Free for the coach. Creates a real Stripe subscription at 100% off, so it shows on the Subscriptions page and survives webhooks. Access stops at the start of the date below.",
  },
  {
    mode: "trial",
    title: "Start a 14-day trial",
    description:
      "The standard self-serve, card-free trial on the Individual plan. After 14 days it cancels and access ends unless the coach upgrades in the app. Not a comp.",
  },
  {
    mode: "none",
    title: "No plan yet",
    description:
      "Free tier. Comp a plan or start a trial later from the team page. If the coach has a pending card-free trial from the app, it is cancelled — admin-created teams never auto-trial.",
  },
]

/**
 * The access decision for a new team: comp, trial, or nothing. One choice, made
 * up front, with the consequences written next to each option — the old form
 * offered a "Plan" dropdown and a trial checkbox side by side, and picking the
 * plan looked like a comp but was a database-only grant nothing in Stripe knew
 * about.
 */
export function TeamAccessChooser({ value, onChange, plans, disabled }: Props) {
  const compablePlans = plans.filter((plan) => plan.tier !== "enterprise")
  const selectedPlan = plans.find((plan) => plan.id === value.planId)

  return (
    <fieldset className="space-y-3" disabled={disabled}>
      <legend className="block text-sm font-medium mb-1">Access</legend>
      <div className="space-y-2">
        {OPTIONS.map((option) => {
          const selected = value.mode === option.mode
          return (
            <label
              key={option.mode}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                selected ? "border-primary bg-primary/5" : "border-border"
              }`}
            >
              <input
                type="radio"
                name="team-access-mode"
                value={option.mode}
                checked={selected}
                onChange={() => onChange({ ...value, mode: option.mode })}
                className="mt-1 h-4 w-4 border-border text-primary focus:ring-primary"
                data-testid={`team-access-${option.mode}`}
              />
              <span>
                <span className="block text-sm font-medium">{option.title}</span>
                <span className="block text-xs text-secondary mt-0.5">{option.description}</span>
              </span>
            </label>
          )
        })}
      </div>

      {value.mode === "comp" && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <div>
            <label htmlFor="team-access-plan" className="block text-sm font-medium mb-2">
              Plan
            </label>
            <select
              id="team-access-plan"
              value={value.planId}
              onChange={(e) => onChange({ ...value, planId: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              {compablePlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                  {plan.stripe_price_id_yearly ? "" : " (no Stripe price)"}
                </option>
              ))}
            </select>
            {selectedPlan && !selectedPlan.stripe_price_id_yearly && (
              <p className="text-xs text-secondary mt-1">
                This plan has no yearly Stripe price. Where Stripe is configured the comp is
                refused before the team is created; set the price under Subscription Plans first.
              </p>
            )}
          </div>
          <Input
            id="team-access-expires"
            label="Ends at the start of"
            type="date"
            value={value.expiresAt}
            onChange={(e) => onChange({ ...value, expiresAt: e.target.value })}
            helperText="Defaults to one year from today. Clear it for a comp with no end date."
          />
          <Input
            id="team-access-reason"
            label="Reason"
            placeholder="diagrams welcome offer 2026, accepted 2026-09-15"
            value={value.reason}
            onChange={(e) => onChange({ ...value, reason: e.target.value })}
            required
            helperText="Required. Stored on the Stripe subscription and the billing row; it is what marks this as a comp."
          />
        </div>
      )}
    </fieldset>
  )
}
